import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import {
  Loader2,
  CheckCircle2,
  RefreshCw,
  Search,
  Shield,
  ExternalLink,
  QrCode,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import AuditTrailExport from './AuditTrailExport';

interface ComplianceProofExplorerProps {
  companyId: string;
}

interface ProofRow {
  ref_type: string;
  ref_id: string;
  event_type: string;
  chain: string;
  tx_hash: string | null;
  sha256: string;
  verifier_url: string | null;
  status: string;
  event_timestamp: string;
  context_name: string;
}

export default function ComplianceProofExplorer({ companyId }: ComplianceProofExplorerProps) {
  const [rows, setRows] = useState<ProofRow[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState<string>('');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState<ProofRow | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  async function load() {
    setLoading(true);
    try {
      const { data, error } = (await supabase.rpc('list_compliance_proofs', {
        p_company: companyId,
      })) as { data: ProofRow[] | null; error: any };

      if (error) {
        console.error('Error loading proofs:', error);
        toast.error('Failed to load compliance proofs');
      } else {
        setRows(data || []);
      }
    } catch (error: any) {
      console.error('Error loading proofs:', error);
      toast.error('Failed to load compliance proofs');
    } finally {
      setLoading(false);
    }
  }

  async function verify(refType: string, refId: string) {
    setVerifying(refId);
    try {
      const { data, error } = await supabase.functions.invoke('verify-proof', {
        body: { refType, refId },
      });

      if (error) {
        console.error('Verification error:', error);
        toast.error('Verification failed');
      } else if (data?.ok) {
        toast.success('✓ Proof verified on-chain');
      } else {
        toast.error('Verification failed');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      toast.error('Verification failed');
    } finally {
      setVerifying('');
      load();
    }
  }

  async function showQrCode(proof: ProofRow) {
    setSelectedProof(proof);
    try {
      const { data, error } = await supabase.functions.invoke('generate-qr-code', {
        body: {
          url: proof.verifier_url || `https://basescan.org/tx/${proof.tx_hash}`,
          size: 300,
        },
      });

      if (error) throw error;

      // Convert blob to URL
      const blob = new Blob([data], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      setQrCodeUrl(url);
      setQrDialogOpen(true);
    } catch (error: any) {
      console.error('QR code error:', error);
      toast.error('Failed to generate QR code');
    }
  }

  useEffect(() => {
    if (companyId) {
      load();
    }
    return () => {
      if (qrCodeUrl) URL.revokeObjectURL(qrCodeUrl);
    };
  }, [companyId]);

  const filtered = rows.filter(
    (r) =>
      !filter ||
      r.ref_id.toLowerCase().includes(filter.toLowerCase()) ||
      r.event_type.toLowerCase().includes(filter.toLowerCase()) ||
      r.context_name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <>
      <div className="space-y-4">
        <Card className="border-border bg-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle>Compliance Proof Explorer</CardTitle>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by ID, type, or name..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            {loading ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length ? (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Name/Context</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Chain</TableHead>
                      <TableHead>Transaction</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => (
                      <TableRow key={r.ref_id}>
                        <TableCell>
                          <Badge variant="outline">{r.ref_type}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{r.context_name}</TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">{r.event_type}</span>
                        </TableCell>
                        <TableCell>
                          <Badge>{r.chain}</Badge>
                        </TableCell>
                        <TableCell>
                          {r.tx_hash ? (
                            <a
                              href={r.verifier_url || `https://basescan.org/tx/${r.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1 text-xs"
                            >
                              {r.tx_hash.slice(0, 12)}...
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={r.status === 'Anchored' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => showQrCode(r)}
                              disabled={!r.tx_hash}
                            >
                              <QrCode className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => verify(r.ref_type, r.ref_id)}
                              disabled={verifying === r.ref_id || !r.tx_hash}
                            >
                              {verifying === r.ref_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No compliance proofs yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  On-chain receipts will appear here as events are notarized
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <AuditTrailExport companyId={companyId} />
      </div>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Proof QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 py-4">
            {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />}
            {selectedProof && (
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">Event: {selectedProof.event_type}</p>
                <p className="text-xs text-muted-foreground font-mono">
                  Chain: {selectedProof.chain}
                </p>
                {selectedProof.verifier_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(selectedProof.verifier_url!, '_blank')}
                  >
                    View on Explorer
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
